import type { Activity, ActivityFlowConsents } from "@lib/activity";
import {
  applyStageBranding,
  type LandingPageChoice,
  resolvePage,
} from "@lib/landing";
import { type ConfigPreview, usePreviewListeners } from "@lib/listeners.client";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Blocks } from "../blocks/blocks";
import { PreviewFonts } from "../preview/fonts";
import { HighlightContainer } from "../preview/highlight";
import { resolveReviewPage } from "@lib/review";
import {
  type Block,
  type GenericLandingPage,
  type GenericReview,
} from "@lib/config/types";
import type { Ticket } from "@lib/ticket";
import { FormStateContext } from "../forms/landing/form-state-context";

type PreviewProps = {
  activity: Activity;
  page: LandingPageChoice | number;
  type: string;
  flowConsents: ActivityFlowConsents;
  config?: ConfigPreview["config"];
  env?: ConfigPreview["env"];
  ticket?: Ticket;
  showWipFeature?: boolean; // Intentionally unused. Placeholder code for ease of future AB tests
};

export function Preview(props: PreviewProps) {
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [state, setState] = useState<ConfigPreview | null>(
    props.config && props.env
      ? {
          config: props.config,
          env: props.env,
          viewPath: [],
        }
      : null,
  );
  const methods = useForm();
  const onSubmit = (_data: unknown) => null;

  usePreviewListeners({
    onUpdate: (data) => {
      setState(data);
    },
  });
  if (!state) return null;

  // Guard against stale loader data after HMR or mid-flight config updates:
  // if the page index is out of bounds for the current config, return null
  // and wait for the UpdateConfig message to deliver the correct config.
  if (props.type === "review") {
    const reviewPages = (state.config as GenericReview)?.pages;
    const pageIndex = Number(props.page);
    if (
      !Array.isArray(reviewPages) ||
      pageIndex < 0 ||
      pageIndex >= reviewPages.length
    ) {
      return null;
    }
  }

  const blocks: Block[] =
    props.type === "review"
      ? resolveReviewPage(
          state.config as GenericReview,
          props.page as string,
          props.activity,
        ) || []
      : resolvePage(
          state.config as GenericLandingPage,
          props.page as LandingPageChoice,
        );

  return (
    <main
      className="min-h-screen"
      style={applyStageBranding(state.config.branding)}
    >
      <PreviewFonts {...state} />

      <FormStateContext.Provider
        value={{
          disableSubmit,
          setDisableSubmit,
          validationErrorsWithoutControl: [],
        }}
      >
        <FormProvider {...methods}>
          <form
            className="min-h-screen grid justify-items-center items-start content-start"
            onSubmit={(e) => void methods.handleSubmit(onSubmit)(e)}
          >
            <HighlightContainer>
              {Blocks({
                blocks,
                ...props,
                ...state,
                ...(props.type === "review" && { flowType: "review" }),
              })}
            </HighlightContainer>
          </form>
        </FormProvider>
      </FormStateContext.Provider>
    </main>
  );
}
